/**
 * Convert a snake_case identifier to Title Case for UI display, e.g.
 * ``"cuecs_identified_flag"`` → ``"Cuecs Identified Flag"``. Used as the
 * fallback label when a field isn't in one of the SOC-1 label maps below.
 */
export const titleCaseFromSnake = (key: string): string => {
  const normalized = key.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Maps a top-level schema field name (from the SOC-1 report-extraction schema)
 * to the user-facing section label matching the SOC-1 review template's tabs.
 * Values not present in this map fall back to a formatted version of the key.
 */
export const SOC1_TAB_LABELS: Record<string, string> = {
  tab_a_summary: "Report Review",
  systems_and_locations_covered: "Systems & Locations Covered",
  applications_covered: "Applications Covered",
  deficiencies: "Deficiencies",
  subservice_organizations: "Subservice Organizations",
  cuecs: "CUECs",
  control_objectives: "Control Objectives",
};

/**
 * Synthetic item_key used to render Tab A's scalar summary as a peer tab in
 * the section switcher. No such key exists in the extraction schema — the
 * viewer synthesises Tab A rows from the scalar fields so every workpaper
 * tab shares the same tab-strip + table interaction pattern.
 */
export const TAB_A_SUMMARY_KEY = "tab_a_summary";

/**
 * Ordering for SOC-1 sections in the viewer. Report Review sits first, then
 * the two Tab-A subsections (Systems & Locations, Applications) because they
 * conceptually belong to Tab A of the workpaper. Deficiencies through
 * Control Objectives follow in template order. Fields not listed here
 * appear after the ordered fields, in insertion order.
 */
export const SOC1_SECTION_ORDER: readonly string[] = [
  TAB_A_SUMMARY_KEY,
  "systems_and_locations_covered",
  "applications_covered",
  "deficiencies",
  "subservice_organizations",
  "cuecs",
  "control_objectives",
];

/**
 * Per-array display labels for the array-section table headers, matching the
 * SOC-1 template's column headers. Keys are `<item_key>.<field_name>` so a
 * single lookup covers every array field. Any field not listed here falls back
 * to a formatted version of the raw snake_case name.
 *
 * Labels are shortened where the template header is long enough to become a
 * poor UI column header (e.g. Tab C column F, Tab E column D).
 */
export const SOC1_COLUMN_LABELS: Record<string, Record<string, string>> = {
  tab_a_summary: {
    field: "Evaluation Item",
    value: "Response",
  },
  deficiencies: {
    section_reference: "Scope / Section",
    related_control_objective: "Control Objective",
    remediation_plan: "Service Org's Remediation Plan",
  },
  subservice_organizations: {
    provider_name: "Provider Name",
    treatment: "Treatment (Carve-out / Inclusive / No Reference)",
    references: "References to Subservice Organization",
    csoc_description: "Complementary Subservice Org Controls (CSOC)",
    related_control_objectives: "Relevant Control Objective / Activity",
  },
  cuecs: {
    related_control_objective: "Related Control Objective",
    description: "CUEC Considerations",
  },
  control_objectives: {
    description: "Control Objective",
    control_activities: "Control Activity Description",
    test_procedures: "Test Procedures Performed",
    test_results: "Test Results / Exceptions Noted",
  },
};

/**
 * Per-scalar display labels for the Tab A summary card, matching the template's
 * row labels. Shortened where the template label is long enough to look ugly
 * in a two-column label/value layout. Fields not listed fall back to the
 * generic `formatFieldLabel` (snake_case → Title Case).
 */
export const SOC1_SCALAR_LABELS: Record<string, string> = {
  report_name: "SOC Report Name",
  report_type: "SOC Report Type",
  period_type: "Report Period Type",
  period_covered_from: "Period Covered — From",
  period_covered_to: "Period Covered — To",
  systems_and_locations_covered: "Systems & Locations Covered",
  service_auditor_name: "Service Auditor Name",
  opinion_type: "Opinion Type",
  deficiencies_noted_flag: "Deficiencies / Exceptions Noted?",
  subservice_orgs_identified_flag: "Subservice Organizations Identified?",
  subservice_treatment: "Subservice Treatment",
  cuecs_identified_flag: "CUECs Identified?",
  standard_used: "Standard the Report Was Prepared Under",
};
